import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Quote } from "src/app/models/Quote.model";
import { RootStoreState } from "src/app/root-store";
import { ClearRentalUpdate } from "src/app/root-store/kwikspace-app-store/actions/rental.action";
import { getCurrentUser } from "src/app/root-store/main-app-store/selectors/auth.selector";
import { KwikspaceService } from "src/app/services/kwikspace.service";
import { AppRoutes } from "src/app/shared/endpoints.enum";

@Component({
  selector: "app-view-rental",
  templateUrl: "./view-rental.component.html",
  styleUrls: ["./view-rental.component.scss"],
})
export class ViewRentalComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject();
  private cancelationSubscription = new Subscription();

  @Input() data: Quote;
  quote: Quote;

  isStaff: boolean = false;

  downloadQuoteLoading: boolean = false;
  displayEscalation: boolean = false;

  monthlyRental = 0;
  damageDeposit = 0;
  initTransport = 0;

  initSubTotal = 0;
  initSubVat = 0;
  monthlySubVat = 0;
  initTotal = 0;
  monthlyTotal = 0;

  yearTwo: boolean = false;
  yearThree: boolean = false;

  yearOneMonths = 0;
  yearOneAmount = 0;
  yearTwoMonths = 0;
  yearTwoAmount = 0;
  yearThreeMonths = 0;
  yearThreeAmount = 0;

  contractTotal = 0;
  contractLength = 0;

  constructor(
    private store: Store<RootStoreState.RootState>,
    private service: KwikspaceService,
    private router: Router,
    private formBuilder: FormBuilder
  ) { }

  ngOnInit() {
    this.store.dispatch(new ClearRentalUpdate());

    this.store
      .select(getCurrentUser)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res) {
          this.isStaff =
            res.isStaff && res.salesRep != null && res.customer == null
              ? true
              : false;
        }
      });

    if (this.data) {
      this.handlePricing();
    }
  }

  handlePricing() {
    this.data.lineItems.map((lineItem) => {
      if (lineItem.rentalOption.category.name.toLowerCase().includes("classroom")) {
        this.populateClassroomPricing(lineItem);
      } else {
        this.populatePricing(lineItem);
      }
    })

    this.calculateTotals();
    this.calculateEscalation(this.data);
  }

  populatePricing(lineItem) {
    const unitQty = lineItem.quantity ? lineItem.quantity : 1;

    lineItem.initPmt =
      lineItem.discountAmount != null
        ? lineItem.discountAmount * unitQty
        : lineItem.rentalOption.monthlyRate * unitQty;
    lineItem.unitDeposit =
      parseFloat(lineItem.discountAmount != null
        ? lineItem.discountAmount
        : lineItem.rentalOption.monthlyRate);
    lineItem.deposit =
      lineItem.discountAmount != null
        ? lineItem.discountAmount * unitQty
        : lineItem.rentalOption.monthlyRate * unitQty;
    lineItem.productCost = 0;

    if (lineItem.productConfigurations.length > 0) {
      let extras = lineItem.productConfigurations;
      let totalExtrasPrice = 0;

      extras.forEach((extra) => {
        let qty = extra.quantity ? extra.quantity : 1;
        let price = parseFloat(extra.product.monthlyRate);
        extra.unitPrice = price.toFixed(2);
        extra.initPmt = (qty * price).toFixed(2);
        extra.monthlyPmt = qty * price;
        extra.period = lineItem.rentalPeriod;

        lineItem.unitDeposit += price * (qty / unitQty);
        totalExtrasPrice += extra.monthlyPmt;
      });

      lineItem.deposit += totalExtrasPrice;
      lineItem.productCost += totalExtrasPrice;
    }

    let deliveryCharge = 0;
    let deliveryChargeExtra = 0;
    let deliveryChargeJoin = 0;

    lineItem.deliveryChargeQty =
      unitQty ? unitQty * 2 : 2;
    lineItem.deliveryChargeExtraQty =
      unitQty ? unitQty * 2 : 2;
    lineItem.deliveryChargeJoinQty =
    unitQty ? unitQty * 2 : 2;
    lineItem.deliveryChargeDiscountedQty =
    unitQty ? unitQty * 2 : 2;

    if (lineItem.deliveryChargeDiscounted == null) {
      if (lineItem.deliveryCharge && lineItem.deliveryCharge.price) {
        lineItem.deliveryCharge.initPrice = parseFloat(
          lineItem.deliveryCharge.price
        );
        deliveryCharge = lineItem.deliveryCharge.price =
          parseFloat(lineItem.deliveryCharge.price) * lineItem.deliveryChargeQty;
      }

      if (lineItem.deliveryChargeExtra && lineItem.deliveryChargeExtra.price) {
        lineItem.deliveryChargeExtra.initPrice = parseFloat(
          lineItem.deliveryChargeExtra.price
        );
        deliveryChargeExtra = lineItem.deliveryChargeExtra.price =
          parseFloat(lineItem.deliveryChargeExtra.price) * lineItem.deliveryChargeExtraQty;
      }

      if (
        lineItem.deliveryChargeOptional &&
        lineItem.deliveryChargeOptional.price
      ) {
        lineItem.deliveryChargeOptional.initPrice = parseFloat(
          lineItem.deliveryChargeOptional.price
        );
        deliveryChargeJoin = lineItem.deliveryChargeOptional.price =
          parseFloat(lineItem.deliveryChargeOptional.price) * lineItem.deliveryChargeJoinQty;
      }
    } else {
      deliveryCharge =
        lineItem.deliveryChargeDiscounted &&
          lineItem.deliveryChargeDiscounted.discountAmount
          ? (lineItem.deliveryChargeDiscounted.discountAmount =
            parseFloat(lineItem.deliveryChargeDiscounted.discountAmount) * 2)
          : 0;

      deliveryChargeJoin =
        lineItem.deliveryChargeOptional && lineItem.deliveryChargeOptional.price
          ? (lineItem.deliveryChargeOptional.price =
            parseFloat(lineItem.deliveryChargeOptional.price) * 2)
          : 0;
    }

    lineItem.initSub = (
      parseFloat(lineItem.initPmt) +
      lineItem.deposit +
      lineItem.productCost +
      deliveryCharge +
      deliveryChargeExtra +
      deliveryChargeJoin
    ).toFixed(2);
    lineItem.monthlySub = (parseFloat(lineItem.initPmt) + lineItem.productCost).toFixed(
      2
    );

    this.damageDeposit += parseFloat(lineItem.deposit);
    this.monthlyRental += parseFloat(lineItem.monthlySub);
    this.initTransport += deliveryCharge + deliveryChargeExtra + deliveryChargeJoin;
  }

  populateClassroomPricing(lineItem) {
    const unitQty = lineItem.quantity ? lineItem.quantity : 1;

    lineItem.initPmt =
      lineItem.discountAmount != null
        ? lineItem.discountAmount * unitQty
        : lineItem.rentalOption.monthlyRate * unitQty;
    lineItem.unitDeposit =
      parseFloat(lineItem.discountAmount != null
        ? lineItem.discountAmount
        : lineItem.rentalOption.monthlyRate);
    lineItem.deposit =
      lineItem.discountAmount != null
        ? lineItem.discountAmount * unitQty
        : lineItem.rentalOption.monthlyRate * unitQty;
    lineItem.productCost = 0;

    if (lineItem.productConfigurations.length > 0) {
      let extras = lineItem.productConfigurations;
      let totalExtrasPrice = 0;

      extras.forEach((extra) => {
        let qty = extra.quantity ? extra.quantity : 1;
        let price = parseFloat(extra.product.monthlyRate);
        extra.unitPrice = price.toFixed(2);
        extra.initPmt = (qty * price).toFixed(2);
        extra.monthlyPmt = qty * price;
        extra.period = lineItem.rentalPeriod;

        lineItem.unitDeposit += price * (qty / unitQty);
        totalExtrasPrice += extra.monthlyPmt;
      });

      lineItem.deposit += totalExtrasPrice;
      lineItem.productCost += totalExtrasPrice;
    }

    let deliveryCharge = 0;
    let deliveryChargeExtra = 0;
    let deliveryChargeJoin = 0;

    lineItem.deliveryChargeQty = unitQty * 2;
    lineItem.deliveryChargeExtraQty = unitQty * 2;
    lineItem.deliveryChargeJoinQty = unitQty * 2;
    lineItem.deliveryChargeDiscountedQty =
      lineItem.deliveryChargeQty != null ? lineItem.deliveryChargeQty * 2 : 2;

    if (lineItem.deliveryChargeDiscounted == null) {
      if (lineItem.deliveryCharge && lineItem.deliveryCharge.price) {
        lineItem.deliveryCharge.initPrice = parseFloat(
          lineItem.deliveryCharge.price
        );
        deliveryCharge = lineItem.deliveryCharge.price =
          parseFloat(lineItem.deliveryCharge.price) * lineItem.deliveryChargeQty;
      }

      if (lineItem.deliveryChargeExtra && lineItem.deliveryChargeExtra.price) {
        lineItem.deliveryChargeExtra.initPrice = parseFloat(
          lineItem.deliveryChargeExtra.price
        );
        deliveryChargeExtra = lineItem.deliveryChargeExtra.price =
          parseFloat(lineItem.deliveryChargeExtra.price) * lineItem.deliveryChargeExtraQty;
      }

      if (
        lineItem.deliveryChargeOptional &&
        lineItem.deliveryChargeOptional.price
      ) {
        lineItem.deliveryChargeOptional.initPrice = parseFloat(
          lineItem.deliveryChargeOptional.price
        );
        deliveryChargeJoin = lineItem.deliveryChargeOptional.price =
          parseFloat(lineItem.deliveryChargeOptional.price) * lineItem.deliveryChargeJoinQty;
      }
    } else {
      deliveryCharge =
        lineItem.deliveryChargeDiscounted &&
          lineItem.deliveryChargeDiscounted.discountAmount
          ? (lineItem.deliveryChargeDiscounted.discountAmount =
            parseFloat(lineItem.deliveryChargeDiscounted.discountAmount) * 2)
          : 0;

      deliveryChargeJoin =
        lineItem.deliveryChargeOptional && lineItem.deliveryChargeOptional.price
          ? (lineItem.deliveryChargeOptional.price =
            parseFloat(lineItem.deliveryChargeOptional.price) * 2)
          : 0;
    }

    lineItem.initSub = (
      parseFloat(lineItem.initPmt) +
      lineItem.productCost +
      lineItem.deposit +
      deliveryCharge +
      deliveryChargeExtra +
      deliveryChargeJoin
    ).toFixed(2);
    lineItem.monthlySub = (parseFloat(lineItem.initPmt) + lineItem.productCost).toFixed(
      2
    );

    this.damageDeposit += parseFloat(lineItem.deposit);
    this.monthlyRental += parseFloat(lineItem.monthlySub);
    this.initTransport += deliveryCharge + deliveryChargeExtra + deliveryChargeJoin;
  }

  calculateTotals() {
    this.initSubTotal =
      this.monthlyRental + this.damageDeposit + this.initTransport;

    this.initTotal = this.initSubTotal * 1.15;
    this.monthlyTotal = this.monthlyRental * 1.15;

    this.initSubVat = this.initTotal - this.initSubTotal;
    this.monthlySubVat = this.monthlyTotal - this.monthlyRental;
  }

  calculateEscalation(quote) {
    if (quote.lineItems.length > 0) {
      this.contractLength = quote.lineItems[0].rentalPeriod;
      if (this.contractLength <= 12) {
        this.yearOneMonths = this.contractLength;
        this.yearOneAmount = this.calculateYearlyAmount(
          this.monthlyRental,
          this.contractLength,
          0
        );
      } else if (this.contractLength > 12 && this.contractLength <= 24) {
        this.displayEscalation = true;
        this.yearTwo = true;
        this.yearOneMonths = 12;
        this.yearOneAmount = this.calculateYearlyAmount(
          this.monthlyRental,
          12,
          0
        );
        this.yearTwoMonths = this.contractLength - 12;
        this.yearTwoAmount = this.calculateYearlyAmount(
          this.monthlyRental,
          this.yearTwoMonths,
          0.1
        );
      } else if (this.contractLength > 24) {
        this.displayEscalation = true;
        this.yearTwo = true;
        this.yearThree = true;
        this.yearOneMonths = 12;
        this.yearOneAmount = this.calculateYearlyAmount(
          this.monthlyRental,
          12,
          0
        );
        this.yearTwoMonths = 12;
        this.yearTwoAmount = this.calculateYearlyAmount(
          this.monthlyRental,
          12,
          0.1
        );
        this.yearThreeMonths = this.contractLength - 24;
        const yearTwoAmount = this.yearTwoAmount / 12;
        this.yearThreeAmount = this.calculateYearlyAmount(
          yearTwoAmount,
          this.yearThreeMonths,
          0.1
        );
      }
    }
    this.contractTotal =
      this.yearOneAmount +
      this.yearTwoAmount +
      this.yearThreeAmount +
      this.damageDeposit +
      this.initTransport;
    this.quote = quote;
  }

  calculateYearlyAmount(rental, months, escalation) {
    let price = 0;
    let rental_price = rental * months;
    price = rental_price + rental_price * escalation;

    return price;
  }

  back() {
    const route = AppRoutes.PWA + AppRoutes.QUOTE_LIST;
    this.router.navigate([route]);
  }

  download() {
    this.downloadQuoteLoading = true;
    this.service.downloadQuote(this.data.id).subscribe((response) => {
      const contentType = "application/pdf";
      const blob = new Blob([response], { type: contentType });
      const downloadUrl = URL.createObjectURL(blob);

      const pdfName = `${this.data.quoteNumber}_${this.data.customer.company.name}.pdf`;
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = pdfName;

      a.click();
      setTimeout(() => {
        this.downloadQuoteLoading = false;
        URL.revokeObjectURL(downloadUrl);
      }, 100);
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.cancelationSubscription.unsubscribe();
  }
}
